import { CompAdjustmentResult } from "@/components/ScatterPlotSummary";
import RegressionModel from "@/types/RegressionModel";
import * as d3 from "d3";
import { subYears } from "date-fns";
import { NormalizedDataPoint } from "./PreparedDataset";
import { AnyRegressionResult, regression } from "./stats";

export type CompPoint = {
  id: string;
  date: Date;
  address: string;
  price: number;
};

type AdjustedCompData = {
  id: string;
  date: Date;
  address: string;
  price: number;
  trendOnCompDate: number;
  compDiffFromTrend: number;
  trendOnSubjectDate: number;
  adjustedValue: number;
  adjustmentAmount: number;
  percentChange: number;
};

function getAdjustedCompData(
  comp: CompPoint,
  regressionData: AnyRegressionResult,
  subjectSaleDate: number,
): AdjustedCompData | null {
  const actualPrice = comp.price;
  const trendOnCompDate = regressionData.predict(comp.date.getTime());
  if (trendOnCompDate === null) return null;
  const compDiffFromTrend = actualPrice - trendOnCompDate;
  const trendOnSubjectDate = regressionData.predict(subjectSaleDate);
  if (trendOnSubjectDate === null) return null;
  const adjustedValue = trendOnSubjectDate + compDiffFromTrend;
  const adjustmentAmount = adjustedValue - comp.price;

  return {
    id: comp.id,
    date: comp.date,
    address: comp.address,
    price: comp.price,
    trendOnCompDate: trendOnCompDate,
    compDiffFromTrend: compDiffFromTrend,
    trendOnSubjectDate: trendOnSubjectDate,
    adjustedValue: adjustedValue,
    adjustmentAmount: adjustmentAmount,
    percentChange: ((adjustedValue - comp.price) / comp.price) * 100,
  };
}

type DeriveYearOverYearTrendInput = {
  // regressionData: AnyRegressionResult;
  // chartStuff: ChartStuff | null;
  regressionData: AnyRegressionResult | null;
  effectiveDate: Date | null;
};
type YearOverYear = {
  currentDate: Date;
  currentValue: number;
  lastYearDate: Date;
  lastYearValue: number;
  percentChange: number;
};
function deriveYearOverYearTrend(input: DeriveYearOverYearTrendInput): YearOverYear | null {
  const { regressionData, effectiveDate } = input;
  if (!effectiveDate) {
    return null;
  }

  const effectiveDateLastYear = effectiveDate ? subYears(effectiveDate, 1) : null;

  let valueLastYear = null;
  try {
    valueLastYear = effectiveDateLastYear
      ? regressionData?.predict(effectiveDateLastYear.getTime()) || 0
      : null;
  } catch (error) {
    console.error(error);
    return null;
  }

  if (!valueLastYear || !effectiveDateLastYear) {
    return null;
  }

  let currentValue = null;
  try {
    currentValue = regressionData?.predict(effectiveDate.getTime()) || 0;
  } catch (error) {
    console.error(error);
    return null;
  }

  return {
    currentDate: effectiveDate,
    currentValue,
    lastYearDate: effectiveDateLastYear,
    lastYearValue: valueLastYear,
    percentChange: ((currentValue - valueLastYear) / valueLastYear) * 100,
  };
}

export function deriveChartStuff(
  data: NormalizedDataPoint[],
  regressionType: RegressionModel | null,
  subject: { date: Date; name: string },
): ChartStuff {
  const subjectSaleDate = subject?.date ? subject.date.getTime() : undefined;

  const comps = data.filter((d) => d.comp);

  const dataXMax = subjectSaleDate
    ? d3.max([...data.map((i) => i.date.getTime()), subjectSaleDate])
    : d3.max(data.map((i) => i.date.getTime()));

  const dataXMin = d3.min(data.map((i) => i.date.getTime()));

  const padding = (dataXMax && dataXMin && (dataXMax - dataXMin) * 0.02) || 0;

  const domain =
    dataXMin && dataXMax ? new Array(dataXMin - padding, dataXMax + padding) : undefined;

  const regressionData = regressionType
    ? regression(
        regressionType,
        data.filter((i) => !i.outlier).map((i) => ({ x: i.date.getTime(), y: i.price })),
        domain,
      )
    : null;

  const adjustedComps =
    regressionData && subjectSaleDate
      ? comps.map((comp) => {
          return {
            comp,
            adjustedData: getAdjustedCompData(comp, regressionData, subjectSaleDate),
          };
        })
      : null;

  const adjustedCompData =
    adjustedComps && subjectSaleDate
      ? adjustedComps.map((i) => {
          return {
            compId: i.comp.id,
            subjectSaleDate,
            adjustedData: i.adjustedData,
          };
        })
      : null;

  const yearOverYear = deriveYearOverYearTrend({ regressionData, effectiveDate: subject.date });

  const compAdjustmentResults: CompAdjustmentResult[] =
    adjustedCompData
      ?.map((i) => {
        if (!i.adjustedData) return null;
        return {
          id: i.compId,
          address: i.adjustedData.address,
          adjustedValue: i.adjustedData.adjustedValue,
          saleDate: i.adjustedData.date,
          salePrice: i.adjustedData.price,
          adjustmentAmount: i.adjustedData.adjustmentAmount,
          percentChange: i.adjustedData.percentChange,
          adjustedPrice: i.adjustedData.adjustedValue,
        };
      })
      .filter((i) => i !== null) || [];

  return {
    data,
    regressionData,
    adjustedCompData,
    dataXMin,
    dataXMax,
    padding,
    yearOverYear,
    compAdjustmentResults,
  };
}

export type ChartStuff = {
  data: NormalizedDataPoint[];
  regressionData: AnyRegressionResult | null;
  adjustedCompData:
    | {
        compId: string;
        subjectSaleDate: number;
        adjustedData: AdjustedCompData | null;
      }[]
    | null;
  dataXMin: number | undefined;
  dataXMax: number | undefined;
  padding: number;
  yearOverYear: YearOverYear | null;
  compAdjustmentResults: CompAdjustmentResult[];
};
